import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useMutation } from '@tanstack/react-query'
import { ApiError, api } from '../../lib'
import { ErrorBox } from '../../app/ui'
import { fileLink, putFile } from './common'

const MAX_BYTES = 10 * 1024 * 1024
const ACCEPT = '.csv,.tsv,.txt,.xlsx,text/csv,text/tab-separated-values,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'

export function UploadCard({ pid, canEdit }: { pid: string; canEdit: boolean }) {
  const nav = useNavigate()
  const [over, setOver] = useState(false)
  const up = useMutation({
    mutationFn: async (file: File) => {
      if (file.size === 0) throw new Error('This file is empty.')
      if (file.size > MAX_BYTES) throw new Error('This file is larger than 10 MB.')
      const made = await api<{ batch_id: string; upload_url: string }>('POST', `/api/partners/${pid}/uploads`, {
        filename: file.name, size: file.size,
      })
      // The file exists now; even when storing it fails, its page says why and offers a retry.
      await putFile(made.upload_url, file).catch((e: unknown) => {
        if (e instanceof ApiError && e.status >= 500) return
        throw e
      })
      return made.batch_id
    },
    onSuccess: (id) => nav(fileLink(id)),
  })
  const pick = (f: File | undefined) => f && up.mutate(f)
  return (
    <div className="stack">
      <p className="muted">Upload a new file every month.</p>
      {canEdit ? (
        <label
          className="panel stack"
          data-testid="ingest-drop"
          style={{ border: `1.5px dashed ${over ? 'var(--blue)' : 'var(--line-strong)'}`, borderRadius: 12, padding: '3rem 1.5rem', textAlign: 'center', cursor: 'pointer' }}
          onDragOver={(e) => { e.preventDefault(); setOver(true) }}
          onDragLeave={() => setOver(false)}
          onDrop={(e) => { e.preventDefault(); setOver(false); pick(e.dataTransfer.files?.[0]) }}
        >
          <strong>{up.isPending ? 'Uploading…' : 'Drop a file or click to pick one'}</strong>
          <span className="muted">CSV or Excel</span>
          <input type="file" accept={ACCEPT} data-testid="ingest-upload-file" style={{ display: 'none' }}
            disabled={up.isPending} onChange={(e) => pick(e.target.files?.[0])} />
        </label>
      ) : (
        <p className="muted">Only partner owners and admins can send files.</p>
      )}
      {up.error && <ErrorBox error={up.error} />}
      <p><a href="/api/csv-template" data-testid="ingest-template">See an example</a></p>
    </div>
  )
}
